import {Box, Button, TextField} from '@mui/material';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {reOwnerApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {ListParamsType} from 'src/types';
import {ResponsiveTable} from '../../ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {ApartmentOutlined} from '@mui/icons-material';
import {titleCellSx} from './ReOwnersTable.sx';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {FilterBar} from 'src/components/common/FilterBar';
import {GridColumns} from '@mui/x-data-grid';
import {FormattedMessage, useIntl} from 'react-intl';
import {TableIdCell} from '../../TableIdCell';
import {ReOwnersTableProps} from './ReOwnersTable.types';
import {ReOwnerActions} from './ReOwnerActions';
import dayjs from 'dayjs';
import AMPModal from '../../AMPModal';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ReOwnerDTO} from 'src/types/reOwner';

const calculateColumnReOwners = (
    businessId: number,
    onUpdate: (id: number) => void,
    onDelete: (id: number) => void
): GridColumns => {
    return [
        {
            field: 'name',
            renderHeader: () => <FormattedMessage id={'name'}/>,
            flex: 2,
            minWidth: 200,
            sortable: false,
            cellClassName: 'ampTableCell',
            renderCell: (params) => (
                <TableIdCell
                    label={params.row.name}
                    link={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.reOwner, params.row.id])}
                    businessId={businessId}
                />
            ),
        },
        {
            field: 'createdAt',
            renderHeader: () => <FormattedMessage id={'createdAt'}/>,
            flex: 1,
            minWidth: 150,
            sortable: false,
            cellClassName: 'ampTableCell',
            renderCell: (params) => dayjs(params.row.createdAt).format('DD MMM YYYY'),
        },
        {
            field: 'actions',
            renderHeader: () => '',
            width: 100,
        sortable: false,
            cellClassName: 'ampTableCell',
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderCell: (params) => (
                <ReOwnerActions
                    realEstateId={params.row.id}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                />
            ),
        },
    ];
};

export const ReOwnersTable: FC<ReOwnersTableProps> = ({
                                                          isDialogVisible,
                                                          title,
                                                          onAddClick,
                                                          buttonText,
                                                          onlyTitle,
                                                          icon,
                                                      }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();

    const [reOwnersTrigger, reOwnersData] = reOwnerApi.useLazyGetReOwnersQuery();
    const [updateReOwner] = reOwnerApi.useUpdateReOwnerMutation();
    const [deleteReOwner] = reOwnerApi.useDeleteReOwnerMutation();

    const [tableParams, setTableParams] = useState<ListParamsType>({
        page: 0,
        size: 100,
    });

    // Modal states
    const [selectedReOwner, setSelectedReOwner] = useState<ReOwnerDTO | null>(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [updateName, setUpdateName] = useState('');

    const initialDataFetch = async () => {
        try {
            await reOwnersTrigger({
                params: {
                    size: tableParams.size,
                    page: tableParams.page,
                },
                data: {
                    businessId,
                },
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

    const handleChangePage = (e: number) => {
        setTableParams((prevState) => ({
            ...prevState,
            page: e,
        }));
    };

    const handlePerPageChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +e.target.value;
        if (typeof val === 'number') {
            setTableParams({size: val, page: 0});
        }
    };

    const handleOpenUpdateModal = (realEstateId: number) => {
        const reOwner = reOwnersData?.data?.content?.find(r => r.id === realEstateId);
        if (reOwner) {
            setSelectedReOwner(reOwner);
            setUpdateName(reOwner.name);
            setIsUpdateModalOpen(true);
        }
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedReOwner(null);
        setUpdateName('');
    };

    const handleDelete = async (realEstateId: number) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await deleteReOwner({
                realEstateId,
                businessId,
            }).unwrap();
            NotifySuccess(intl.formatMessage({id: 'deletedSuccessfully'}));
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleUpdate = async () => {
        if (!selectedReOwner || !updateName.trim()) return;

        try {
            dispatch(preloaderChangeStatus(true));
            await updateReOwner({
                realEstateId: selectedReOwner.id,
                data: {
                    businessId,
                    name: updateName.trim(),
                },
            }).unwrap();
            NotifySuccess(intl.formatMessage({id: 'updatedSuccessfully'}));
            handleCloseUpdateModal();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    useEffect(() => {
        if (!isDialogVisible) {
            void initialDataFetch();
        }
    }, [tableParams, isDialogVisible]);

    return (
        <>
            <TableLayout
                toolbar={(
                    <FilterBar
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        subtitle={intl.formatMessage(
                            {id: 'rowsFound'},
                            {count: reOwnersData?.data?.page?.totalElements ?? 0}
                        )}
                    />
                )}
                totalPages={reOwnersData?.data?.page?.totalPages ?? 0}
                page={tableParams.page}
                pageSize={tableParams.size}
                onPageChange={handleChangePage}
                onPageSizeChange={handlePerPageChange}
            >
                <ResponsiveTable
                    responsiveElement={
                        <CardList>
                            {reOwnersData?.data?.content?.map((reOwner) => (
                                <TableCard key={reOwner.id}>
                                    <PhoneCardHead
                                        icon={<ApartmentOutlined/>}
                                        title={(
                                            /* The column's own name cell, worn as the title: the
                                               same path and the same tenant switch as the row,
                                               with only its type made the card's. */
                                            <Box sx={titleCellSx}>
                                                <TableIdCell
                                                    label={reOwner.name}
                                                    link={AppRouteSpace([
                                                        AppRouteEnum.app,
                                                        AppRouteEnum.reOwner,
                                                        String(reOwner.id),
                                                    ])}
                                                    businessId={businessId}
                                                    withoutCopy
                                                />
                                            </Box>
                                        )}
                                    />
                                    <PhoneCardFacts
                                        facts={[
                                            {
                                                label: <FormattedMessage id="createdAt"/>,
                                                value: dayjs(reOwner.createdAt).format('DD MMM YYYY'),
                                            },
                                        ]}
                                    />
                                </TableCard>
                            ))}
                        </CardList>
                    }
                    desktopElement={
                        <Table
                            loading={reOwnersData?.isLoading}
                            rows={reOwnersData?.data?.content ?? []}
                            hideFooter
                            columns={calculateColumnReOwners(businessId, handleOpenUpdateModal, handleDelete)}
                        />
                    }
                />
            </TableLayout>

            {/* Update Modal */}
            <AMPModal
                handleClose={handleCloseUpdateModal}
                open={isUpdateModalOpen}
                title={intl.formatMessage({id: 'editReOwner'})}
                type="sm"
                fullWidth
            >
                <Box sx={{p: 3}}>
                    <TextField
                        fullWidth
                        label={intl.formatMessage({id: 'name'})}
                        value={updateName}
                        onChange={(e) => setUpdateName(e.target.value)}
                        sx={{mb: 3}}
                    />
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                        <Button variant="outlined" onClick={handleCloseUpdateModal}>
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdate}
                            disabled={!updateName.trim()}
                        >
                            <FormattedMessage id="save"/>
                        </Button>
                    </Box>
                </Box>
            </AMPModal>
        </>
    );
};
